import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { PropsWithChildren } from "react";
import { FormattedMessage } from "react-intl";

import BlockAccordion from "@components/accordions/BlockAccordion";
import styles from "@components/account/Account.module.css";
import SecurityAccountDeletion from "@components/account/SecurityAccountDeletion";
import SecurityMarketing from "@components/account/SecurityMarketing";
import SecurityWorkEmailResend from "@components/account/SecurityWorkEmailResend";
import TwoFactorAuthenticationSetup from "@components/account/TwoFactorAuthenticationSetup";
import ClientPasswordUpdateForm from "@components/forms/ClientPasswordUpdateForm";
import EmailSecurityForm from "@components/forms/EmailSecurityForm";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import { AccountSectionEnum } from "@typing/Enums";
import { GetClientWithAccountQuery } from "@typing/Generated";
import { Direction } from "@typing/Models";

type Props = {
  client: GetClientWithAccountQuery["client"];
  handleCurrentSectionChange: (section: AccountSectionEnum, direction: Direction) => void;
};

type SectionProp = PropsWithChildren<{
  titleKey: string;
}>;

const Section = ({ children, titleKey }: SectionProp) => (
  <div className={styles.accountSectionContainer}>
    <div className={styles.accountSectionHeader}>
      <BodyCopy color="gray-500">
        <FormattedMessage id={titleKey} />
      </BodyCopy>
    </div>
    {children}
  </div>
);

type OpenableSectionProps = PropsWithChildren<{
  handleCurrentSectionChange: (newSection: AccountSectionEnum, direction: Direction) => void;
  openSection: AccountSectionEnum;
}>;

const OpenableSection = ({ children, handleCurrentSectionChange, openSection }: OpenableSectionProps) => (
  <div>
    <div className={styles.openableSection}>
      <div className="ion-hide-lg-up">
        <ButtonLink
          className="full-width"
          onClick={() => handleCurrentSectionChange(openSection, "right")}
          stylized={false}
        >
          <IonGrid className={`ion-no-padding ${styles.mobileOpenableSection}`}>
            <IonRow>
              <IonCol size="auto">
                <div className="no-wrap">
                  <Heading2 bold color="black">
                    <FormattedMessage id={`components.AccountMenu.${openSection}`} />
                  </Heading2>
                </div>
              </IonCol>
              <IonCol>
                <div className="ion-text-end">
                  <Icon color="gray-300" icon="chevronRight" />
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </ButtonLink>
      </div>
      <div className="ion-hide-lg-down">{children}</div>
    </div>
  </div>
);

const AccountPrivacy = ({ client, handleCurrentSectionChange }: Props) => (
  <div>
    <Section titleKey="pages.client.account.privacyAndAccount.sections.privacy">
      <OpenableSection
        handleCurrentSectionChange={handleCurrentSectionChange}
        openSection={AccountSectionEnum.PRIVACY_TWO_FACTOR}
      >
        <TwoFactorAuthenticationSetup client={client} />
      </OpenableSection>
      <OpenableSection
        handleCurrentSectionChange={handleCurrentSectionChange}
        openSection={AccountSectionEnum.PRIVACY_CHANGE_PASSWORD}
      >
        <BlockAccordion
          border={false}
          color="transparent"
          content={<ClientPasswordUpdateForm autofocus={false} />}
          heading={
            <Heading2 bold color="black">
              <FormattedMessage id="pages.client.account.privacyAndAccount.sections.changePassword" />
            </Heading2>
          }
        />
      </OpenableSection>
    </Section>
    <Section titleKey="pages.client.account.privacyAndAccount.sections.notifications">
      <OpenableSection
        handleCurrentSectionChange={handleCurrentSectionChange}
        openSection={AccountSectionEnum.PRIVACY_EMAIL_NOTIFICATIONS}
      >
        <BlockAccordion
          border={false}
          color="transparent"
          content={<EmailSecurityForm client={client} />}
          heading={
            <Heading2 bold color="black">
              <FormattedMessage id="pages.client.account.privacyAndAccount.sections.emailPreferences" />
            </Heading2>
          }
        />
      </OpenableSection>
      <OpenableSection
        handleCurrentSectionChange={handleCurrentSectionChange}
        openSection={AccountSectionEnum.PRIVACY_MARKETING}
      >
        <BlockAccordion
          border={false}
          color="transparent"
          content={<SecurityMarketing client={client} />}
          heading={
            <Heading2 bold color="black">
              <FormattedMessage id="pages.client.account.privacyAndAccount.sections.marketingAndSms" />
            </Heading2>
          }
        />
      </OpenableSection>
    </Section>
    <Section titleKey="pages.client.account.privacyAndAccount.sections.account">
      {client.needsWorkEmailVerification && client.workEmail && <SecurityWorkEmailResend client={client} />}
      <OpenableSection
        handleCurrentSectionChange={handleCurrentSectionChange}
        openSection={AccountSectionEnum.PRIVACY_ACCOUNT_DELETION}
      >
        <BlockAccordion
          border={false}
          color="transparent"
          content={<SecurityAccountDeletion />}
          heading={
            <Heading2 bold color="black">
              <FormattedMessage id="pages.client.account.deleteMyAccount" />
            </Heading2>
          }
        />
      </OpenableSection>
    </Section>
  </div>
);

export default AccountPrivacy;
